import React, { useState, useEffect } from 'react';
import { Badge } from '@mui/material';
import { ChatBubbleOutline as ChatIcon } from '@mui/icons-material';
import { useSendbirdStateContext } from '@sendbird/uikit-react';
import { useSendBirdReady } from 'services/sendBird/sendBirdService';

/**
 * Chat icon component with unread message count badge
 * Only shows the badge when Sendbird is available and has unread messages
 */
const ChatIconWithBadge = () => {
    const [unreadCount, setUnreadCount] = useState(0);
    const ready = useSendBirdReady();

    // Safely get the Sendbird context
    let sbCtx = null;
    let sdk = null;

    try {
        sbCtx = useSendbirdStateContext();
        sdk = sbCtx?.stores?.sdkStore?.sdk;
    } catch {
        // Sendbird context is not available
        console.debug('Sendbird context not available for chat icon');
    }

    useEffect(() => {
        if (!ready || !sdk || !sdk.groupChannel) {
            setUnreadCount(0);
            return;
        }

        const updateUnreadCount = async () => {
            try {
                const count = await sdk.groupChannel.getTotalUnreadMessageCount();
                setUnreadCount(count || 0);
            } catch (error) {
                console.error('Failed to get unread message count:', error);
                setUnreadCount(0);
            }
        };

        // Initial count
        updateUnreadCount();

        // Set up a simple interval to check for updates instead of event handlers
        const interval = setInterval(updateUnreadCount, 5000); // Check every 5 seconds

        // Cleanup
        return () => {
            clearInterval(interval);
        };
    }, [ready, sdk]);

    // Show badge only if there are unread messages
    const badgeContent = unreadCount > 0 ? (unreadCount > 9 ? '9+' : unreadCount) : null;

    if (badgeContent) {
        return (
            <Badge
                color='primary'
                badgeContent={badgeContent}
                showZero={false}
                sx={{
                    '& .MuiBadge-badge': {
                        backgroundColor: '#1976d2',
                        color: '#fff',
                        right: -3,
                        top: -3,
                        fontSize: 12,
                        minWidth: 20,
                        height: 20,
                    },
                }}
            >
                <ChatIcon />
            </Badge>
        );
    }

    return <ChatIcon />;
};

export default ChatIconWithBadge;
